import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import ScorePanel from './components/ScorePanel';
import ExtraInfoPanel from './components/ExtraInfoPanel';
import { Army, ARMY_CONFIG, PlayerMeta } from './armyConfig';
import { getArmyDisplayName } from './utils/armyUtils';

const ROUNDS = 4;
const LOCAL_STORAGE_KEY = 'aos-spearhead-state-v1';

export type Player = 'Attacker' | 'Defender';

export interface GameState {
  attackerArmy: Army | undefined;
  defenderArmy: Army | undefined;
  scores: { attacker: number[]; defender: number[] };
  extra: { attacker: PlayerMeta; defender: PlayerMeta };
}

function getDefaultPlayerMeta(army: Army | undefined): PlayerMeta {
  if (!army) {
    return { notes: '', armyFields: [], units: [] };
  }
  const config = ARMY_CONFIG[army];
  return {
    notes: '',
    armyFields: (config.armyFields || []).map(f => ({ 
      key: f.key, 
      value: f.type === 'integer' ? (typeof f.default === 'number' ? f.default : 0) : false 
    })),
    units: (config.units || []).map(u => ({
      id: u.id,
      label: '',
      metaFields: u.metaFields.map(f => ({ 
        key: f.key, 
        value: f.type === 'integer' ? (typeof f.default === 'number' ? f.default : 0) : false 
      }))
    })),
  };
}

const defaultState: GameState = {
  attackerArmy: undefined,
  defenderArmy: undefined,
  scores: { attacker: Array(ROUNDS).fill(undefined), defender: Array(ROUNDS).fill(undefined) },
  extra: {
    attacker: getDefaultPlayerMeta(undefined),
    defender: getDefaultPlayerMeta(undefined),
  },
};

function loadState(): GameState {
  const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (!raw) return defaultState;
  try {
    const parsed = JSON.parse(raw);
    if (
      (parsed.attackerArmy && !Object.values(Army).includes(parsed.attackerArmy)) ||
      (parsed.defenderArmy && !Object.values(Army).includes(parsed.defenderArmy))
    ) {
      throw new Error('Invalid army in saved data');
    }
    // Convert nulls to undefined in scores arrays
    const fixScoreArray = (arr: any[]) => Array.isArray(arr) ? arr.slice(0, ROUNDS).map(v => v === null ? undefined : v) : Array(ROUNDS).fill(undefined);
    return {
      ...defaultState,
      ...parsed,
      scores: {
        attacker: fixScoreArray(parsed.scores?.attacker),
        defender: fixScoreArray(parsed.scores?.defender),
      },
      extra: {
        attacker: parsed.extra?.attacker || getDefaultPlayerMeta(parsed.attackerArmy),
        defender: parsed.extra?.defender || getDefaultPlayerMeta(parsed.defenderArmy),
      },
    };
  } catch {
    alert('Saved data is invalid or out of date. The app has been reset to default.');
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    return defaultState;
  }
}

function saveState(state: GameState) {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
}

function getEmojiSummary(state: GameState): string {
  const attacker = state.attackerArmy;
  const defender = state.defenderArmy;
  // Use fallback names if armies are not selected
  const attackerName = attacker ? getArmyDisplayName(attacker) : 'Attacker';
  const defenderName = defender ? getArmyDisplayName(defender) : 'Defender';
  const attackerTotal = state.scores.attacker.reduce((a, b) => a + (b ?? 0), 0);
  const defenderTotal = state.scores.defender.reduce((a, b) => a + (b ?? 0), 0);

  // Header
  let lines: string[] = [];
  lines.push(`*${attackerName}* vs *${defenderName}*`);

  // Rounds
  for (let i = 0; i < state.scores.attacker.length; i++) {
    const a = state.scores.attacker[i] ?? 0;
    const d = state.scores.defender[i] ?? 0;
    lines.push(`${a} - ${d}`);
  }

  // Totals
  lines.push('');
  lines.push(`*${attackerTotal}* - *${defenderTotal}*`);
  lines.push('');

  // Winner/Draw line
  if (attackerTotal > defenderTotal) {
    lines.push(`*Winner: ${attackerName}*`);
  } else if (defenderTotal > attackerTotal) {
    lines.push(`*Winner: ${defenderName}*`);
  } else if (attackerTotal === defenderTotal) {
    lines.push(`*Draw*`);
  }
  return lines.join('\n');
}

function setAppVh() {
  const vh = window.visualViewport ? window.visualViewport.height : window.innerHeight;
  document.documentElement.style.setProperty('--app-vh', `${vh / 100}px`);
}

function App() {
  const [state, setState] = useState<GameState>(() => loadState());
  const [openPanel, setOpenPanel] = useState<'score' | 'extra'>('score');
  const [copyStatus, setCopyStatus] = useState<string>('');
  const [currentRound, setCurrentRound] = useState(0);
  const [wakeLockActive, setWakeLockActive] = useState(false);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const [fixedViewportHeight, setFixedViewportHeight] = useState<number>(0);
  const [resetKey, setResetKey] = useState(0);
  const [isWide, setIsWide] = useState(false);

  // Capture initial viewport height on mount
  useEffect(() => {
    const captureHeight = () => {
      // Use visualViewport if available, otherwise fallback to window.innerHeight
      const height = window.visualViewport?.height || window.innerHeight;
      setFixedViewportHeight(height);
    };
    
    captureHeight();
    // Also capture on resize in case of orientation change
    window.addEventListener('resize', captureHeight);
    return () => window.removeEventListener('resize', captureHeight);
  }, []);

  // Set --app-vh CSS variable on mount and on viewport changes
  useEffect(() => {
    setAppVh();
    const handleResize = () => setAppVh();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  useEffect(() => {
    saveState(state);
  }, [state]);

  // Listen for visibility change and external wake lock release
  useEffect(() => {
    const handleVisibility = async () => {
      if (wakeLockActive && document.visibilityState === 'visible') {
        try {
          if ('wakeLock' in navigator) {
            wakeLockRef.current = await (navigator as any).wakeLock.request('screen');
            if (wakeLockRef.current) {
              wakeLockRef.current.addEventListener('release', () => setWakeLockActive(false));
            }
          }
        } catch {
          setWakeLockActive(false);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [wakeLockActive]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      wakeLockRef.current?.release();
    };
  }, []);

  // Listen for external wake lock release
  useEffect(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.onrelease = () => setWakeLockActive(false);
    }
  }, [wakeLockActive]);

  const toggleWakeLock = async () => {
    if (!wakeLockActive) {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await (navigator as any).wakeLock.request('screen');
          setWakeLockActive(true);
          if (wakeLockRef.current) {
            wakeLockRef.current.addEventListener('release', () => setWakeLockActive(false));
          }
        }
      } catch {
        setWakeLockActive(false);
      }
    } else {
      await wakeLockRef.current?.release();
      setWakeLockActive(false);
    }
  };

  const handleArmyChange = (player: Player, army: Army) => {
    setState((prev) => {
      const prevMeta = prev.extra[player.toLowerCase() as 'attacker' | 'defender'];
      const newExtra = {
        ...getDefaultPlayerMeta(army),
        notes: prevMeta.notes,
      };
      return {
        ...prev,
        [`${player.toLowerCase()}Army`]: army,
        extra: {
          ...prev.extra,
          [player.toLowerCase()]: newExtra,
        },
      } as GameState;
    });
  };

  const handleScoreChange = (player: Player, round: number, value: number) => {
    setState((prev) => {
      const scores = { ...prev.scores };
      scores[player.toLowerCase() as 'attacker' | 'defender'] = [...scores[player.toLowerCase() as 'attacker' | 'defender']];
      scores[player.toLowerCase() as 'attacker' | 'defender'][round] = value;
      return { ...prev, scores };
    });
  };

  const handleNotesChange = (player: Player, value: string) => {
    setState((prev) => ({
      ...prev,
      extra: {
        ...prev.extra,
        [player.toLowerCase()]: {
          ...prev.extra[player.toLowerCase() as 'attacker' | 'defender'],
          notes: value,
        },
      },
    }));
  };

  const handleArmyFieldChange = (player: Player, fieldKey: string, value: number | boolean) => {
    setState((prev) => {
      const playerMeta = prev.extra[player.toLowerCase() as 'attacker' | 'defender'];
      const newArmyFields = playerMeta.armyFields.map(f =>
        f.key === fieldKey ? { ...f, value } : f
      );
      return {
        ...prev,
        extra: {
          ...prev.extra,
          [player.toLowerCase()]: {
            ...playerMeta,
            armyFields: newArmyFields,
          },
        },
      };
    });
  };

  const handleUnitFieldChange = (player: Player, unitId: string, fieldKey: string, value: number | boolean) => {
    setState((prev) => {
      const playerMeta = prev.extra[player.toLowerCase() as 'attacker' | 'defender'];
      const newUnits = playerMeta.units.map(unit => {
        if (unit.id !== unitId) return unit;
        return {
          ...unit,
          metaFields: unit.metaFields.map(f =>
            f.key === fieldKey ? { ...f, value } : f
          ),
        };
      });
      return {
        ...prev,
        extra: {
          ...prev.extra,
          [player.toLowerCase()]: {
            ...playerMeta,
            units: newUnits,
          },
        },
      };
    });
  };

  const handleUnitLabelChange = (player: Player, unitId: string, label: string) => {
    setState((prev) => {
      const playerMeta = prev.extra[player.toLowerCase() as 'attacker' | 'defender'];
      const newUnits = playerMeta.units.map(unit =>
        unit.id === unitId ? { ...unit, label } : unit
      );
      return {
        ...prev,
        extra: {
          ...prev.extra,
          [player.toLowerCase()]: {
            ...playerMeta,
            units: newUnits,
          },
        },
      };
    });
  };

  const handleClear = () => {
    setState({
      ...defaultState,
      attackerArmy: undefined,
      defenderArmy: undefined,
    });
    setCopyStatus('');
    setCurrentRound(0);
    setResetKey(k => k + 1);
  };

  const handleCopy = async () => {
    const summary = getEmojiSummary(state);
    try {
      await navigator.clipboard.writeText(summary);
      setCopyStatus('Copied!');
      setTimeout(() => setCopyStatus(''), 1500);
    } catch {
      setCopyStatus('Failed to copy');
      setTimeout(() => setCopyStatus(''), 1500);
    }
  };

  const totalAttacker = state.scores.attacker.reduce((a, b) => a + (b ?? 0), 0);
  const totalDefender = state.scores.defender.reduce((a, b) => a + (b ?? 0), 0);

  // Responsive: determine if wide layout should be used
  useEffect(() => {
    const checkWidth = () => setIsWide(window.innerWidth >= 900);
    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  return (
    <div className={`App${isWide ? ' mainLayout' : ''}`} style={{ height: 'calc(var(--app-vh, 1vh) * 100)', background: '#000', color: '#fff', padding: 0, fontFamily: 'inherit', display: isWide ? 'flex' : 'flex', flexDirection: isWide ? 'row' : 'column', boxSizing: 'border-box', maxWidth: isWide ? 1200 : 480, margin: '0 auto', position: 'relative', overflow: 'hidden' }}>
      <img
        src={`${process.env.PUBLIC_URL}/warhammeroctopus.png`}
        alt="AoS Spearhead Logo"
        style={{ width: 72, height: 72, margin: '16px auto 0 auto', display: 'block', borderRadius: 16, boxShadow: '0 2px 8px #0008', padding: 12, background: '#111' }}
      />

      <div className={isWide ? 'sidePanel' : ''} style={{ flex: isWide ? '1 1 0' : undefined, minWidth: 0, background: isWide ? '#111' : undefined, borderRight: isWide ? '1px solid #333' : undefined, display: 'flex', flexDirection: 'column' }}>
        {!isWide && (
          <div
            onClick={() => setOpenPanel('score')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: openPanel === 'score' ? '#333' : '#181a1b',
              padding: '18px 20px',
              fontWeight: 'bold',
              fontSize: 18,
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              borderBottom: '1px solid #444',
              cursor: openPanel === 'score' ? 'default' : 'pointer',
              userSelect: 'none',
            }}
          >
            <span>Score</span>
          </div>
        )}
        <div
          style={isWide ? { flex: 1, minHeight: 0, background: '#222', borderBottomLeftRadius: 12, borderBottomRightRadius: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '16px' } : {
            width: '100%',
            background: '#222',
            padding: openPanel === 'score' ? '0' : '0',
            borderBottomLeftRadius: openPanel !== 'score' ? 12 : 0,
            borderBottomRightRadius: openPanel !== 'score' ? 12 : 0,
            overflow: 'hidden',
            maxHeight: openPanel === 'score' ? (fixedViewportHeight ? `${fixedViewportHeight - 120 - 54 - 54}px` : 'calc(100dvh - 120px - 54px - 54px)') : 0,
            opacity: openPanel === 'score' ? 1 : 0,
            transition: 'max-height 0.4s cubic-bezier(.4,2,.6,1), opacity 0.3s',
            boxSizing: 'border-box',
            flex: openPanel === 'score' ? '1 1 auto' : '0 1 0',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {(isWide || openPanel === 'score') && (
            <ScorePanel
              key={resetKey}
              attackerArmy={state.attackerArmy}
              defenderArmy={state.defenderArmy}
              armies={ARMY_CONFIG}
              scores={state.scores}
              onArmyChange={handleArmyChange}
              onScoreChange={handleScoreChange}
              totalAttacker={totalAttacker}
              totalDefender={totalDefender}
              onCopy={handleCopy}
              onClear={handleClear}
              copyStatus={copyStatus}
              rounds={ROUNDS}
              currentRound={currentRound}
              setCurrentRound={setCurrentRound}
            />
          )}
        </div>
      </div>

      <div className={isWide ? 'sidePanel' : ''} style={{ flex: isWide ? '1 1 0' : undefined, minWidth: 0, background: isWide ? '#181a1b' : undefined, display: 'flex', flexDirection: 'column' }}>
        {!isWide && (
          <div
            onClick={() => setOpenPanel('extra')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: openPanel === 'extra' ? '#333' : '#181a1b',
              padding: '18px 20px',
              fontWeight: 'bold',
              fontSize: 18,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              borderBottom: '1px solid #444',
              cursor: openPanel === 'extra' ? 'default' : 'pointer',
              userSelect: 'none',
            }}
          >
            <span>Extra info</span>
          </div>
        )}
        <div
          style={isWide ? { flex: 1, minHeight: 0, padding: '16px 0', display: 'flex', flexDirection: 'column', gap: 0 } : {
            width: '100%',
            background: '#222',
            padding: openPanel === 'extra' ? '16px' : '0',
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            overflow: 'hidden',
            maxHeight: openPanel === 'extra' ? (fixedViewportHeight ? `${fixedViewportHeight - 120 - 54 - 54}px` : 'calc(100dvh - 120px - 54px - 54px)') : 0,
            opacity: openPanel === 'extra' ? 1 : 0,
            transition: 'max-height 0.4s cubic-bezier(.4,2,.6,1), opacity 0.3s',
            boxSizing: 'border-box',
            flex: openPanel === 'extra' ? '1 1 auto' : '0 1 0',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {(isWide || openPanel === 'extra') && (
            <ExtraInfoPanel
              attackerArmy={state.attackerArmy}
              defenderArmy={state.defenderArmy}
              attackerMeta={state.extra.attacker}
              defenderMeta={state.extra.defender}
              armyConfig={ARMY_CONFIG}
              onNotesChange={handleNotesChange}
              onArmyFieldChange={handleArmyFieldChange}
              onUnitFieldChange={handleUnitFieldChange}
              onUnitLabelChange={handleUnitLabelChange}
              split={isWide}
            />
          )}
        </div>
      </div>

      {/* Wake Lock Button */}
      <button
        onClick={toggleWakeLock}
        aria-label={wakeLockActive ? 'Disable Keep Screen On' : 'Enable Keep Screen On'}
        style={{
          position: 'fixed',
          right: 20,
          bottom: '20px',
          zIndex: 1000,
          width: 40,
          height: 40,
          borderRadius: '50%',
          border: wakeLockActive ? 'none' : '2px solid #fff',
          background: wakeLockActive ? '#2196f3' : '#aaa',
          color: '#fff',
          boxShadow: '0 2px 8px #0008',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.2s, border 0.2s',
          cursor: 'pointer',
        }}
      >
        {wakeLockActive ? (
          // Filled Eye Icon
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5C7 5 2.73 8.11 1 12c1.73 3.89 6 7 11 7s9.27-3.11 11-7c-1.73-3.89-6-7-11-7zm0 12c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8a3 3 0 100 6 3 3 0 000-6z" />
          </svg>
        ) : (
          // Outline Eye Icon
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
}

export default App;
